/* eslint-disable no-plusplus */
import { useCallback, useEffect, useRef, useState } from 'react';
import { updateDevicePreferences } from '../deviceSettingsStorage';

const FULL_SCALE_RMS = 0.6;
const RELEASE_PER_MS = 100 / 500;
const audioContextClass = () =>
  typeof window === 'undefined'
    ? undefined
    : window.AudioContext || window.webkitAudioContext;
const deviceId = track => track?.getSettings?.().deviceId;
const videoTrack = publisher => {
  const source = publisher?.getVideoSource?.();
  return source?.track || source || null;
};
const includeSelected = (options, selected, label) =>
  !selected || options.some(option => option.value === selected)
    ? options
    : [{ label, value: selected }, ...options];

export const useVonageDeviceSettings = ({
  publisher,
  hasVideoSource,
  isMicrophoneEnabled,
  isCameraEnabled,
  initialSettings,
}) => {
  const [microphones, setMicrophones] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [speakers, setSpeakers] = useState([
    { label: 'System default', value: 'default' },
  ]);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState(
    initialSettings?.microphoneDeviceId,
  );
  const [selectedCameraId, setSelectedCameraId] = useState(
    initialSettings?.cameraDeviceId,
  );
  const [selectedSpeakerId, setSelectedSpeakerId] = useState(
    initialSettings?.speakerDeviceId || 'default',
  );
  const selectedSpeakerRef = useRef(
    initialSettings?.speakerDeviceId || 'default',
  );
  const [changing, setChanging] = useState({});
  const [errors, setErrors] = useState({});
  const [micLevel, setMicLevel] = useState(0);
  const [playing, setPlaying] = useState(false);
  const audioElementRef = useRef(null);
  const mounted = useRef(true);
  const micEnabled = useRef(isMicrophoneEnabled);
  const cameraEnabled = useRef(isCameraEnabled);
  const generations = useRef({ microphone: 0, camera: 0, speaker: 0 });
  const queues = useRef({
    microphone: Promise.resolve(),
    camera: Promise.resolve(),
    speaker: Promise.resolve(),
  });
  const meter = useRef({});
  const test = useRef({});
  micEnabled.current = isMicrophoneEnabled;
  cameraEnabled.current = isCameraEnabled;

  const speakerSupported =
    typeof window !== 'undefined' &&
    typeof window.OT?.setAudioOutputDevice === 'function';

  const stopMeter = useCallback(() => {
    if (meter.current.retry) clearTimeout(meter.current.retry);
    if (meter.current.frame != null) cancelAnimationFrame(meter.current.frame);
    const { context } = meter.current;
    meter.current = {};
    if (context?.state !== 'closed') context?.close();
    if (mounted.current) setMicLevel(0);
  }, []);

  const startMeter = useCallback(() => {
    stopMeter();
    const AudioContext = audioContextClass();
    if (!AudioContext || !requestAnimationFrame) return;
    const attach = attempt => {
      const track = publisher?.getAudioSource?.();
      if (!mounted.current) return;
      if (!track) {
        if (attempt < 40)
          meter.current.retry = setTimeout(() => attach(attempt + 1), 250);
        return;
      }
      try {
        const context = new AudioContext();
        const analyser = context.createAnalyser();
        const source = context.createMediaStreamSource(
          new MediaStream([track]),
        );
        const samples = new Uint8Array(analyser.fftSize);
        source.connect(analyser);
        meter.current = { context, level: 0, timestamp: null };
        context.resume().catch(() => {});
        const measure = timestamp => {
          if (!mounted.current || meter.current.context !== context) return;
          analyser.getByteTimeDomainData(samples);
          const rms = Math.sqrt(
            samples.reduce((sum, sample) => {
              const normalized = (sample - 128) / 128;
              return sum + normalized * normalized;
            }, 0) / samples.length,
          );
          const target = Math.min(100, (rms * 100) / FULL_SCALE_RMS);
          if (meter.current.timestamp == null)
            meter.current.timestamp = timestamp;
          const elapsed = Math.max(0, timestamp - meter.current.timestamp);
          meter.current.timestamp = timestamp;
          meter.current.level =
            target >= meter.current.level
              ? target
              : Math.max(
                  target,
                  meter.current.level - RELEASE_PER_MS * elapsed,
                );
          setMicLevel(Math.round(meter.current.level));
          meter.current.frame = requestAnimationFrame(measure);
        };
        meter.current.frame = requestAnimationFrame(measure);
      } catch (error) {
        stopMeter();
      }
    };
    attach(0);
  }, [publisher, stopMeter]);

  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices?.enumerateDevices?.();
      if (!devices || !mounted.current) return;
      let mic = 0;
      let camera = 0;
      let speaker = 0;
      const microphonesFound = devices
        .filter(item => item.kind === 'audioinput')
        .map(item => ({
          value: item.deviceId,
          label: item.label || `Microphone ${++mic}`,
        }));
      const camerasFound = devices
        .filter(item => item.kind === 'videoinput')
        .map(item => ({
          value: item.deviceId,
          label: item.label || `Camera ${++camera}`,
        }));
      const speakersFound = devices
        .filter(
          (item, index, all) =>
            item.kind === 'audiooutput' &&
            item.deviceId !== 'default' &&
            item.deviceId !== 'communications' &&
            all.findIndex(
              candidate =>
                candidate.kind === item.kind &&
                candidate.deviceId === item.deviceId,
            ) === index,
        )
        .map(item => ({
          value: item.deviceId,
          label: item.label || `Speaker ${++speaker}`,
        }));
      setMicrophones(
        includeSelected(
          microphonesFound,
          selectedMicrophoneId,
          'Current microphone',
        ),
      );
      setCameras(
        includeSelected(camerasFound, selectedCameraId, 'Current camera'),
      );
      setSpeakers([
        { label: 'System default', value: 'default' },
        ...includeSelected(
          speakersFound,
          selectedSpeakerId === 'default' ? undefined : selectedSpeakerId,
          'Current speaker',
        ),
      ]);
    } catch (error) {
      // Hot-plug enumeration must not interrupt the call.
    }
  }, [selectedCameraId, selectedMicrophoneId, selectedSpeakerId]);

  const queuedSelection = useCallback(
    (kind, id, setter, sourceMethod, publishMethod, enabledRef) => {
      if (!publisher?.[sourceMethod]) return Promise.resolve(false);
      const generation = ++generations.current[kind];
      setChanging(current => ({ ...current, [kind]: true }));
      setErrors(current => ({ ...current, [kind]: false }));
      const operation = queues.current[kind]
        .catch(() => {})
        .then(async () => {
          if (generation !== generations.current[kind]) return false;
          try {
            await publisher[sourceMethod](id);
            if (!mounted.current) return false;
            publisher[publishMethod](enabledRef.current);
            setter(id);
            updateDevicePreferences({
              [kind === 'camera' ? 'cameraDeviceId' : 'microphoneDeviceId']: id,
            });
            if (kind === 'microphone') startMeter();
            return generation === generations.current[kind];
          } catch (error) {
            if (generation === generations.current[kind])
              setErrors(current => ({ ...current, [kind]: true }));
            return false;
          } finally {
            if (generation === generations.current[kind])
              setChanging(current => ({ ...current, [kind]: false }));
          }
        });
      queues.current[kind] = operation.then(() => {});
      return operation;
    },
    [publisher, startMeter],
  );

  const selectMicrophone = useCallback(
    id =>
      queuedSelection(
        'microphone',
        id,
        setSelectedMicrophoneId,
        'setAudioSource',
        'publishAudio',
        micEnabled,
      ),
    [queuedSelection],
  );
  const selectCamera = useCallback(
    id => {
      if (!hasVideoSource) {
        setErrors(current => ({ ...current, camera: true }));
        return Promise.resolve(false);
      }
      return queuedSelection(
        'camera',
        id,
        setSelectedCameraId,
        'setVideoSource',
        'publishVideo',
        cameraEnabled,
      );
    },
    [hasVideoSource, queuedSelection],
  );
  const selectSpeaker = useCallback(id => {
    if (!window.OT?.setAudioOutputDevice) return false;
    const generation = ++generations.current.speaker;
    setChanging(current => ({ ...current, speaker: true }));
    setErrors(current => ({ ...current, speaker: false }));
    const operation = queues.current.speaker
      .catch(() => {})
      .then(async () => {
        if (generation !== generations.current.speaker) return false;
        const previous = selectedSpeakerRef.current;
        try {
          await window.OT.setAudioOutputDevice(id);
          if (!mounted.current) return false;
          selectedSpeakerRef.current = id;
          setSelectedSpeakerId(id);
          updateDevicePreferences({ speakerDeviceId: id });
          return generation === generations.current.speaker;
        } catch (error) {
          if (generation === generations.current.speaker) {
            setErrors(current => ({ ...current, speaker: true }));
            try {
              await window.OT.setAudioOutputDevice(previous);
              selectedSpeakerRef.current = previous;
            } catch (restoreError) {
              // The previous output may also be unplugged.
            }
          }
          return false;
        } finally {
          if (generation === generations.current.speaker)
            setChanging(current => ({ ...current, speaker: false }));
        }
      });
    queues.current.speaker = operation.then(() => {});
    return operation;
  }, []);

  const stopTest = useCallback(() => {
    if (test.current.element) test.current.element.srcObject = null;
    test.current.stream?.getTracks().forEach(track => track.stop());
    try {
      test.current.oscillator?.stop();
    } catch (error) {
      // It may already have stopped.
    }
    if (test.current.context?.state !== 'closed') test.current.context?.close();
    test.current = {};
    if (mounted.current) setPlaying(false);
  }, []);
  const playSpeakerTest = useCallback(async () => {
    const element = audioElementRef.current;
    const AudioContext = audioContextClass();
    if (!element || !AudioContext) return false;
    stopTest();
    setPlaying(true);
    try {
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const destination = context.createMediaStreamDestination();
      test.current = {
        context,
        oscillator,
        stream: destination.stream,
        element,
      };
      oscillator.connect(gain);
      gain.connect(destination);
      gain.gain.setValueAtTime(0.08, context.currentTime);
      oscillator.frequency.setValueAtTime(440, context.currentTime);
      element.srcObject = destination.stream;
      if (element.setSinkId) await element.setSinkId(selectedSpeakerId);
      await context.resume();
      await element.play();
      await new Promise(resolve => {
        oscillator.onended = resolve;
        oscillator.start();
        oscillator.stop(context.currentTime + 0.2);
      });
      return true;
    } catch (error) {
      return false;
    } finally {
      stopTest();
    }
  }, [selectedSpeakerId, stopTest]);

  useEffect(() => {
    const audioId = deviceId(publisher?.getAudioSource?.());
    const cameraId = deviceId(videoTrack(publisher));
    if (audioId) setSelectedMicrophoneId(audioId);
    if (cameraId) setSelectedCameraId(cameraId);
    if (publisher) startMeter();
  }, [publisher, startMeter]);
  useEffect(() => {
    const onChange = () => enumerateDevices();
    enumerateDevices();
    navigator.mediaDevices?.addEventListener?.('devicechange', onChange);
    return () =>
      navigator.mediaDevices?.removeEventListener?.('devicechange', onChange);
  }, [enumerateDevices]);
  useEffect(
    () => () => {
      mounted.current = false;
      Object.keys(generations.current).forEach(kind => {
        generations.current[kind] += 1;
      });
      stopMeter();
      stopTest();
    },
    [stopMeter, stopTest],
  );

  return {
    microphones,
    cameras,
    speakers,
    selectedMicrophoneId,
    selectedCameraId,
    selectedSpeakerId,
    changing,
    errors,
    micLevel,
    playing,
    speakerSupported,
    audioElementRef,
    selectMicrophone,
    selectCamera,
    selectSpeaker,
    playSpeakerTest,
  };
};
